/**
 * PreSessionModule
 *
 * Short preparation form before a meeting.
 * Fields: weekly win | biggest obstacle | focus question.
 *
 * If requirePreSession is enabled on settings, completion is required
 * before the user can join. Exposes `isCompleted` via callback so the
 * parent hub can gate the "Join Meeting" button.
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  InsightIcon,
  SuccessIcon,
  CheckIcon,
  GoalIcon,
  WarningIcon,
  Loader,
  ICON_STYLES,
} from '../../components/icons';
import {
  collection,
  query,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  hasUserCompletedPreSession,
  savePreSessionReflection,
} from './structuredGrowthService';
import type { StructuredGrowthEntityType, PreSessionReflection } from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PreSessionModuleProps {
  entityId: string;
  entityType: StructuredGrowthEntityType;
  userId: string;
  meetingId?: string;
  /** When true, hides the form if already completed */
  required?: boolean;
  onCompletionChange?: (completed: boolean) => void;
}

// ---------------------------------------------------------------------------
// Field component
// ---------------------------------------------------------------------------

const Field = memo(function Field({
  label,
  icon,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  icon: React.ReactNode;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
        {icon}
        {label}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none text-gray-900 placeholder-gray-400"
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PreSessionModule: React.FC<PreSessionModuleProps> = ({
  entityId,
  userId,
  meetingId,
  required = false,
  onCompletionChange,
}) => {
  const [completed, setCompleted] = useState(false);
  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastReflection, setLastReflection] = useState<PreSessionReflection | null>(null);

  const [weeklyWin, setWeeklyWin] = useState('');
  const [biggestObstacle, setBiggestObstacle] = useState('');
  const [focusQuestion, setFocusQuestion] = useState('');

  const isValid = weeklyWin.trim() && biggestObstacle.trim() && focusQuestion.trim();

  // Check completion and load last reflection on mount
  useEffect(() => {
    let active = true;
    const load = async () => {
      // Single where to avoid composite index — filter client-side
      const q = query(
        collection(db, 'structuredGrowthReflections'),
        where('userId', '==', userId),
        limit(20),
      );
      const snap = await getDocs(q);
      if (!active) return;
      const matches = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as PreSessionReflection))
        .filter((r) => r.entityId === entityId && r.type === 'preSession')
        .sort((a, b) => ((b.completedAt as any)?.seconds ?? 0) - ((a.completedAt as any)?.seconds ?? 0));
      if (matches.length > 0) {
        const data = matches[0];
        setLastReflection(data);
        setCompleted(true);
        onCompletionChange?.(true);
      } else {
        setCompleted(false);
        onCompletionChange?.(false);
      }
      setChecking(false);
    };
    load();
    return () => { active = false; };
  }, [entityId, userId, onCompletionChange]);

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      await savePreSessionReflection({
        entityId,
        userId,
        ...(meetingId ? { meetingId } : {}),
        type: 'preSession',
        weeklyWin: weeklyWin.trim(),
        biggestObstacle: biggestObstacle.trim(),
        focusQuestion: focusQuestion.trim(),
      });
      setLastReflection({
        id: 'local',
        entityId,
        userId,
        ...(meetingId ? { meetingId } : {}),
        type: 'preSession',
        weeklyWin: weeklyWin.trim(),
        biggestObstacle: biggestObstacle.trim(),
        focusQuestion: focusQuestion.trim(),
        completedAt: { toDate: () => new Date() } as any,
      });
      setCompleted(true);
      onCompletionChange?.(true);
    } finally {
      setSaving(false);
    }
  }, [
    isValid,
    entityId,
    userId,
    meetingId,
    weeklyWin,
    biggestObstacle,
    focusQuestion,
    onCompletionChange,
  ]);

  if (checking) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader size={18} strokeWidth={1.5} className="animate-spin text-gray-300" />
      </div>
    );
  }

  if (completed && lastReflection) {
    const date = lastReflection.completedAt?.toDate?.()?.toLocaleDateString('nl-NL', {
      day: 'numeric', month: 'long',
    });
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <InsightIcon size={16} weight="regular" className="text-gray-500" />
            <span className="text-sm font-semibold text-gray-900">Voorbereiding sessie</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{date}</span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
              ✓ Ingevuld
            </span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
            <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1">
              <SuccessIcon size={12} weight="fill" className="text-emerald-500" />
              Wekelijkse overwinning
            </p>
            <p className="text-sm text-gray-800">{lastReflection.weeklyWin}</p>
          </div>
          <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
            <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
              <WarningIcon size={12} weight="fill" className="text-amber-500" />
              Grootste obstakel
            </p>
            <p className="text-sm text-gray-800">{lastReflection.biggestObstacle}</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
            <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1">
              <GoalIcon size={12} weight="regular" className="text-blue-500" />
              Focusvraag
            </p>
            <p className="text-sm text-gray-800">{lastReflection.focusQuestion}</p>
          </div>
        </div>

        <button
          onClick={() => { setCompleted(false); setLastReflection(null); }}
          className="mt-4 text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          Nieuwe voorbereiding invullen →
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <InsightIcon size={16} weight="regular" className="text-gray-500" />
        <span className="text-sm font-semibold text-gray-900">Voorbereiding sessie</span>
      </div>
      <p className="text-xs text-gray-400 mb-5">
        Neem 2 minuten om je voor te bereiden. Dat helpt de kwaliteit van de sessie enorm.
      </p>

      {required && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100 mb-4">
          <WarningIcon size={14} weight="fill" className="text-amber-500 shrink-0" />
          <p className="text-xs text-amber-700">
            Vereist voordat je de meeting kunt joinen.
          </p>
        </div>
      )}

      <div className="space-y-4">
        <Field
          label="Wekelijkse overwinning"
          icon={<SuccessIcon size={13} weight="regular" className="text-emerald-500" />}
          placeholder="Wat ging er goed de afgelopen week?"
          value={weeklyWin}
          onChange={setWeeklyWin}
        />
        <Field
          label="Grootste obstakel"
          icon={<WarningIcon size={13} weight="regular" className="text-amber-500" />}
          placeholder="Waar loop je tegenaan of wat blokkeert je?"
          value={biggestObstacle}
          onChange={setBiggestObstacle}
        />
        <Field
          label="Focusvraag"
          icon={<GoalIcon size={13} weight="regular" className="text-blue-500" />}
          placeholder="Welke vraag wil je vandaag beantwoord hebben?"
          value={focusQuestion}
          onChange={setFocusQuestion}
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={!isValid || saving}
        className="mt-5 w-full h-10 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        <CheckIcon size={14} weight="bold" />
        <span>{saving ? 'Opslaan...' : 'Voorbereiding indienen'}</span>
      </button>
    </div>
  );
};

export default memo(PreSessionModule);
